import React, { useCallback, useEffect, useState } from 'react';
import { Checkbox, Divider, Select, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { searchRegions } from '../../../service/VolunteerApplicationService';
import { displayError } from '../../../shared/Utils';
import { useToastMessage } from '../../../hooks/useToastMessage';

const { Option } = Select;

interface OptionData {
	value: string;
	label: string;
}

interface RemoteSingleSelectProps {
	onChange?: (value: OptionData[]) => void;
	value?: OptionData[] | null | undefined;
	functionarySrcmId?: string;
	isFetchedDataBySRCMID?: boolean;
	isFunctionaryCareTeam: boolean;
}

const RemoteSingleSelect: React.FC<RemoteSingleSelectProps> = ({
	onChange,
	value,
	functionarySrcmId,
	isFetchedDataBySRCMID,
	isFunctionaryCareTeam,
}: RemoteSingleSelectProps) => {
	const { t } = useTranslation();
	const { showToastMessage } = useToastMessage();
	const [options, setOptions] = useState<OptionData[]>([]);
	const [loading, setLoading] = useState(false);

	const [selectedValue, setSelectedValue] = useState<OptionData[]>(value || []);
	const [filteredOptions, setFilteredOptions] = useState<OptionData[]>([]);
	const [open, setOpen] = useState(false);

	const allValues = options.map((opt) => opt.value);
	const selectedValuesOnly = selectedValue
		.map((item) => item.value)
		.filter((val): val is string => val !== undefined);
	const isAllSelected = selectedValuesOnly.length === allValues.length;
	const isIndeterminate = selectedValuesOnly.length > 0 && !isAllSelected;

	const fetchData = useCallback(
		async (searchValue: string) => {
			setLoading(true);

			if (functionarySrcmId) {
				try {
					const regionsPage = await searchRegions({
						functionarySrcmId,
						pageIndex: 0,
						pageSize: 100,
						searchKey: searchValue,
						includeOnlyRegionsOfApprover: true,
						displayAllRegions: true,
						isPaginationRequired: searchValue ? true : false,
					});

					if (regionsPage.regions) {
						const transformedOptions: OptionData[] = regionsPage.regions.map(
							(item: { regionId: string; regionName: string }) => ({
								value: item.regionId,
								label: item.regionName,
							})
						);

						setOptions(transformedOptions);
						setFilteredOptions(transformedOptions);
					}
				} catch (err) {
					const errorMsg = displayError(err);
					showToastMessage('error', errorMsg);
				}
			} else {
				setOptions([]);
			}

			setLoading(false);
		},
		[functionarySrcmId, showToastMessage]
	);

	useEffect(() => {
		void fetchData('');
	}, [fetchData]);

	const handleSearch = (searchKey: string) => {
		setOpen(true);
		if (!searchKey) {
			setFilteredOptions(options);
		} else {
			const lower = searchKey.toLowerCase();
			const filtered = options.filter((item) =>
				item.label?.toLowerCase().includes(lower)
			);
			setFilteredOptions(filtered);
		}
	};

	const handleChange = (values: { label: string; value: string }[]) => {
		const selected = values.map((val) => ({
			value: val.value,
			label: val.label,
		}));
		setSelectedValue(selected);
		onChange?.(selected);
		setFilteredOptions(options);
		setOpen(true);
	};

	const handleSelectAll = () => {
		if (isAllSelected) {
			setSelectedValue([]);
			onChange?.([]);
		} else {
			setSelectedValue(options);
			onChange?.(options);
		}
		setFilteredOptions(options);
	};

	useEffect(() => {
		if (value) {
			setSelectedValue(value);
		}
	}, [value]);

	return (
		<Select
			mode="multiple"
			showSearch
			open={open}
			onDropdownVisibleChange={(o) => setOpen(o)}
			filterOption={false}
			labelInValue
			defaultActiveFirstOption={false}
			popupClassName="hide-ant-checkmark"
			placeholder={t('searchRegions')}
			value={selectedValue as { label: string; value: string }[]}
			onSearch={handleSearch}
			onChange={handleChange}
			notFoundContent={loading ? <Spin size="small" /> : null}
			className="font-sans text-sm text-secondary-gray [&_.ant-select-selector]:!h-[150px] [&_.ant-select-selector]:!overflow-y-auto [&_.ant-select-selector]:!overflow-x-hidden"
			size="large"
			suffixIcon={null}
			disabled={isFetchedDataBySRCMID || isFunctionaryCareTeam}
			optionLabelProp="label"
			dropdownRender={(menu) => (
				<>
					<div
						className="px-2 py-3 cursor-pointer flex justify-between"
						onClick={handleSelectAll}
					>
						<span>Select All</span>
						<Checkbox
							checked={isAllSelected}
							indeterminate={isIndeterminate}
							style={{ marginRight: 8 }}
						/>
					</div>
					<Divider style={{ margin: 0 }} />
					{menu}
				</>
			)}
		>
			{filteredOptions.map((opt) => (
				<Option key={opt.value} value={opt.value} label={opt.label}>
					<span>{opt.label}</span>
				</Option>
			))}
		</Select>
	);
};

export default RemoteSingleSelect;
